import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Star, Presentation } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", price: "", unit: "per session", description: "", features: "", featured: false, order: 0 };

export default function PlansTab() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios
      .get(`${API}/admin/plans`, { withCredentials: true })
      .then((res) => setPlans(res.data))
      .catch(() => toast.error("Could not load plans"));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      name: plan.name,
      price: String(plan.price),
      unit: plan.unit,
      description: plan.description || "",
      features: (plan.features || []).join("\n"),
      featured: plan.featured,
      order: plan.order,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || form.price === "" || isNaN(Number(form.price))) {
      toast.error("Please add a plan name and a valid price.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      price: Number(form.price),
      unit: form.unit || "per session",
      description: form.description || null,
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean).slice(0, 12),
      featured: form.featured,
      order: Number(form.order) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/plans/${editing.id}`, payload, { withCredentials: true });
        toast.success("Plan updated.");
      } else {
        await axios.post(`${API}/admin/plans`, payload, { withCredentials: true });
        toast.success("Plan created.");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Could not save plan.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/plans/${plan.id}`, { withCredentials: true });
      toast.success("Plan deleted.");
      load();
    } catch {
      toast.error("Could not delete plan.");
    }
  };

  if (!plans) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
        <Loader2 size={18} className="animate-spin text-[#D4AF37]" /> Loading plans…
      </div>
    );
  }

  return (
    <div data-testid="plans-content">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-white text-2xl font-bold uppercase tracking-tight">
            Your pricing plans
          </h2>
          <p className="text-zinc-500 text-sm font-light mt-1">
            Private — never shown on the public site. Use Present Mode to show clients in person.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/present")}
            disabled={plans.length === 0}
            data-testid="present-mode-button"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3CE56] disabled:opacity-40 text-zinc-950 px-5 py-3 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
          >
            <Presentation size={15} strokeWidth={2} /> Present Mode
          </button>
          <button
            onClick={openCreate}
            data-testid="add-plan-button"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-5 py-3 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
          >
            <Plus size={15} strokeWidth={2} /> Add Plan
          </button>
        </div>
      </div>

      {plans.length === 0 ? (
        <div
          data-testid="plans-empty-state"
          className="border border-dashed border-zinc-800 p-14 text-center"
        >
          <p className="font-display text-zinc-400 text-xl font-bold uppercase tracking-tight">
            No plans yet
          </p>
          <p className="text-zinc-600 text-sm font-light mt-2">
            Add your first pricing plan — e.g. "1-2-1 Single Session" or "10 Session Block".
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative bg-zinc-900/50 border p-7 transition-colors duration-300 ${
                p.featured ? "border-[#D4AF37]/60" : "border-zinc-800 hover:border-zinc-700"
              }`}
            >
              {p.featured && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[#D4AF37] text-[10px] font-bold tracking-[0.2em] uppercase">
                  <Star size={11} fill="#D4AF37" /> Featured
                </span>
              )}
              <h3 className="font-display text-white text-2xl font-bold uppercase tracking-tight pr-20">
                {p.name}
              </h3>
              <p className="mt-3">
                <span className="font-display text-[#D4AF37] text-4xl font-black tracking-tight">
                  £{Number(p.price) % 1 === 0 ? Number(p.price) : Number(p.price).toFixed(2)}
                </span>
                <span className="text-zinc-500 text-sm ml-2">{p.unit}</span>
              </p>
              {p.description && (
                <p className="mt-3 text-zinc-400 text-sm font-light leading-relaxed">{p.description}</p>
              )}
              {p.features?.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {p.features.map((f, i) => (
                    <li key={i} className="text-zinc-400 text-sm font-light flex gap-2">
                      <span className="text-[#D4AF37]">·</span> {f}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 pt-4 border-t border-zinc-800 flex items-center gap-2">
                <button
                  onClick={() => openEdit(p)}
                  data-testid={`edit-plan-${p.id}`}
                  className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-3.5 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`delete-plan-${p.id}`}
                  className="inline-flex items-center gap-2 border border-zinc-800 hover:border-red-500/60 hover:text-red-400 text-zinc-500 px-3.5 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">
              {editing ? "Edit Plan" : "New Plan"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Prices are private — only visible here and in Present Mode.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Plan Name</Label>
              <Input
                data-testid="plan-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. 1-2-1 Single Session"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Price (£)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                data-testid="plan-form-price"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="40"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Unit</Label>
              <Input
                data-testid="plan-form-unit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="per session / per block / per month"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Description (optional)</Label>
              <Input
                data-testid="plan-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary of what's included"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">
                Features — one per line (optional)
              </Label>
              <Textarea
                data-testid="plan-form-features"
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                rows={4}
                placeholder={"Personalised programme\nNutrition check-ins\nWhatsApp support"}
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.featured}
                onCheckedChange={(v) => setForm({ ...form, featured: v })}
                data-testid="plan-form-featured"
              />
              <Label className="text-xs uppercase tracking-[0.14em] text-zinc-400">Featured plan</Label>
            </div>
            <div>
              <Label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Display order</Label>
              <Input
                type="number"
                data-testid="plan-form-order"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-11"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button
              onClick={save}
              disabled={saving}
              data-testid="plan-form-save"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3CE56] disabled:opacity-60 text-zinc-950 px-7 py-3 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {editing ? "Save Changes" : "Create Plan"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
